import React, { useEffect, useState } from 'react';

import { Mail } from 'lucide-react';

import {
	useApproveReplyDraftMutation,
	useCreateReplyDraftMutation,
	useGetReplyDraftsQuery,
	useUpdateReplyDraftMutation,
} from '../../api/replyDrafts';
import { useToast } from '../../context/ToastContext';

import type { DocumentCheck } from '../../api';
import type { ReplyDraft } from '../../api/replyDrafts';

// Same codes as the API's needs_new_file: only these get a reply.
const NEW_FILE_CODES = new Set([
	'NOT_A_STATEMENT',
	'MISSING_PAGES',
	'UNREADABLE',
]);

export const needsReply = (check?: DocumentCheck | null): boolean =>
	check?.status === 'flagged' &&
	check.issues.some((issue) => NEW_FILE_CODES.has(issue.code));

// v9 .btn / .btn-sm and their colors.
const BTN =
	'inline-flex items-center justify-center gap-2 whitespace-nowrap border font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_MD = `${BTN} px-4 py-2 text-sm leading-5 rounded-lg`;
const BTN_SM = `${BTN} px-3 py-1 text-xs leading-[18px] rounded-md`;
const PRIMARY =
	'bg-primary-500 text-white border-transparent hover:bg-primary-600';
const SECONDARY = 'bg-white text-primary-700 border-gray-300 hover:bg-gray-50';
const NEUTRAL = 'bg-white text-gray-900 border-gray-300 hover:bg-gray-100';
// v9 .mail .mrow
const MAIL_ROW =
	'flex items-center gap-3 border-b border-gray-200 px-4 py-2.5 text-sm';
const MAIL_INPUT = 'flex-1 border-0 py-1 text-sm outline-none focus:ring-0';

const errorText = (error: unknown): string =>
	(error as { data?: { error?: string } })?.data?.error ??
	'Something went wrong. Please try again.';

interface BounceReplyDraftProps {
	fileId: string;
	check?: DocumentCheck | null;
	/** v9: the email is the main action only when the file is not a statement. */
	primary?: boolean;
}

/** v9 "Email <contact>" for a bounced statement: opens the drafted reply for review (ROC-96). */
const BounceReplyDraft: React.FC<BounceReplyDraftProps> = ({
	fileId,
	check,
	primary = false,
}) => {
	const { showToast } = useToast();
	const show = needsReply(check);
	const { data } = useGetReplyDraftsQuery(fileId, { skip: !show });
	const [createDraft, { isLoading: creating }] = useCreateReplyDraftMutation();
	const [updateDraft, { isLoading: saving }] = useUpdateReplyDraftMutation();
	const [approveDraft, { isLoading: sending }] = useApproveReplyDraftMutation();
	const [editing, setEditing] = useState<ReplyDraft | null>(null);

	useEffect(() => {
		if (!editing) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setEditing(null);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [editing]);

	if (!show) return null;
	const latest = data?.drafts[0];
	const pending = latest?.status === 'pending' ? latest : undefined;

	const open = async () => {
		try {
			setEditing(pending ?? (await createDraft(fileId).unwrap()));
		} catch (error) {
			showToast('Email', errorText(error), 'error');
		}
	};

	const send = async () => {
		if (!editing) return;
		try {
			const saved = await updateDraft({
				id: editing.id,
				changes: {
					to_email: editing.to_email || undefined,
					subject: editing.subject,
					body: editing.body,
				},
			}).unwrap();
			await approveDraft(saved.id).unwrap();
			setEditing(null);
			showToast('Email sent', `Sent to ${saved.to_email}.`, 'success');
		} catch (error) {
			showToast('Email', errorText(error), 'error');
		}
	};

	// No "Send again" yet: a check run gets one reply (product question on the PR).
	if (latest && latest.status !== 'pending' && latest.status !== 'discarded') {
		return (
			<span className="text-xs text-gray-500">
				{latest.status === 'sent'
					? `Email sent to ${latest.to_email}${
							latest.sent_at
								? ` · ${new Date(latest.sent_at).toLocaleString()}`
								: ''
						}`
					: latest.status === 'sending'
						? `Sending to ${latest.to_email}…`
						: `Sending to ${latest.to_email} could not be confirmed.`}
			</span>
		);
	}

	return (
		<>
			<button
				type="button"
				className={`${BTN_SM} ${primary ? PRIMARY : SECONDARY}`}
				disabled={creating}
				onClick={open}
			>
				<Mail className="h-3 w-3" aria-hidden />
				Email {pending?.to_email ?? 'the merchant'}
			</button>
			{editing && (
				<div
					className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
					onClick={(e) => {
						if (e.target === e.currentTarget) setEditing(null);
					}}
				>
					<div
						role="dialog"
						aria-modal="true"
						aria-labelledby="bounce-reply-title"
						className="flex max-h-[90vh] w-full max-w-[680px] flex-col rounded-lg bg-white shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1)]"
					>
						<div className="flex items-center justify-between border-b border-gray-200 p-6">
							<h4
								id="bounce-reply-title"
								className="text-xl font-semibold text-gray-900"
							>
								Email the merchant
							</h4>
							<button
								type="button"
								className={`${BTN_SM} ${NEUTRAL}`}
								onClick={() => setEditing(null)}
							>
								Close
							</button>
						</div>
						<div className="overflow-auto p-6">
							<p className="mb-4 text-gray-500">
								The message is ready to send. It says what is wrong and what to
								send. Edit it if you want to add a note.
							</p>
							<div className="overflow-hidden rounded-lg border border-gray-200">
								<div className={MAIL_ROW}>
									<label
										htmlFor="bounce-reply-to"
										className="w-16 flex-none text-gray-500"
									>
										To
									</label>
									<input
										id="bounce-reply-to"
										type="email"
										className={MAIL_INPUT}
										value={editing.to_email ?? ''}
										onChange={(e) =>
											setEditing({ ...editing, to_email: e.target.value })
										}
									/>
								</div>
								<div className={MAIL_ROW}>
									<label
										htmlFor="bounce-reply-subject"
										className="w-16 flex-none text-gray-500"
									>
										Subject
									</label>
									<input
										id="bounce-reply-subject"
										className={MAIL_INPUT}
										value={editing.subject}
										onChange={(e) =>
											setEditing({ ...editing, subject: e.target.value })
										}
									/>
								</div>
								<textarea
									aria-label="Message"
									className="block min-h-[220px] w-full resize-y border-0 p-4 text-sm leading-[1.6] outline-none focus:ring-0"
									value={editing.body}
									onChange={(e) =>
										setEditing({ ...editing, body: e.target.value })
									}
								/>
							</div>
							<p className="mt-3 text-xs text-gray-500">
								Send the new file in reply, then upload it here to check it
								again.
							</p>
						</div>
						<div className="flex justify-end gap-3 border-t border-gray-200 p-6">
							<button
								type="button"
								className={`${BTN_MD} ${NEUTRAL}`}
								onClick={() => setEditing(null)}
							>
								Cancel
							</button>
							<button
								type="button"
								className={`${BTN_MD} ${PRIMARY}`}
								disabled={!editing.to_email || saving || sending}
								onClick={send}
							>
								Send email
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default BounceReplyDraft;
